import { FC, useCallback, useState } from 'react';
import dynamic from 'next/dynamic';
import useCartStore from 'src/hooks/useCart';
import { DrugItem } from 'src/api/types';
import useModalStore from 'src/hooks/useModalStore';
import { useRouter } from 'next/router';
import { FormattedMessage } from 'react-intl';
import { ModalPosition } from 'src/shared-components/Modal/Modal';
import useMedia from 'src/hooks/useMedia';
import { useAnalytics } from 'src/hooks/useAnalytics/useAnalytics';
import useOneDrugOrderStore from 'src/hooks/useOneDrugOrderStore';
import NextImage from '../NextImage/NextImage';
import trns from './trns';
import ButtonSecondary, {
  ButtonSecondarySize
} from '../design-system/ButtonSecondary';
import UnitCounter from '../UnitCounter/UnitCounter';

const Modal = dynamic(() => import('src/shared-components/Modal/Modal'), {
  ssr: false
});

interface Props {
  drug: DrugItem;
  specifyQuantityModal?: string;
}

const closeIcon = 'shared/close.svg';

const SpecifyQuantityModal: FC<Props> = ({ drug, specifyQuantityModal }) => {
  const { addItemToSingleDrugCart } = useCartStore();
  const { setOneDrugOrder, setBackRoute } = useOneDrugOrderStore();
  const { updateModalsConfig } = useModalStore();
  const { sendChangeCartItemsAnalytic } = useAnalytics();
  const router = useRouter();
  const { sm } = useMedia();

  const [quantity, setQuantity] = useState({
    packQuantity: 1,
    unitQuantity: 0
  });

  const pillsInUnit = drug?.quantityInPack / drug?.quantityUnitInPack;

  const handleAddItemToCart = useCallback(
    (drugParam: DrugItem) => {
      router.push('/checkout/cart?step=proposalSelect', undefined, {
        shallow: true
      });
      setTimeout(() => {
        addItemToSingleDrugCart({
          drug: drugParam,
          packQuantity: quantity.packQuantity,
          unitQuantity: quantity.unitQuantity
        }).then(() =>
          sendChangeCartItemsAnalytic({
            drug: drugParam,
            packageQuantityDetails: { packQuantity: quantity.packQuantity }
          })
        );
        setBackRoute(router.asPath);
        setOneDrugOrder(true);
        updateModalsConfig(specifyQuantityModal, false);
      });
    },
    [addItemToSingleDrugCart, quantity.packQuantity, quantity.unitQuantity]
  );

  const handleClose = () => {
    updateModalsConfig(specifyQuantityModal, false);
    setQuantity({ packQuantity: 1, unitQuantity: 0 });
  };

  const handleQuantityChange = async (packQuantity, unitQuantity) => {
    setQuantity({ packQuantity, unitQuantity });
    return Promise.resolve();
  };

  return (
    <>
      <div>
        <Modal
          id={specifyQuantityModal}
          position={
            sm ? ModalPosition.AbsoluteCenter : ModalPosition.RelativeBottom
          }
          onClose={handleClose}
          withShadowBackground
          customHeader={
            <>
              <div className="flex justify-between items-center p-[16px]">
                <p className="fs-header5-bold text-black pr-[16px]">
                  <FormattedMessage {...trns.specifyQuantityOfProduct} />
                </p>
                <div className="flex justify-between items-center">
                  <button
                    onClick={handleClose}
                    className="flex items-center justify-center w-9 h-9"
                  >
                    <NextImage
                      alt="close"
                      src={closeIcon}
                      layout="fixed"
                      height={24}
                      width={24}
                    />
                  </button>
                </div>
              </div>
            </>
          }
        >
          <div className="flex flex-col items-center">
            <div className="flex justify-center mb-6 sm:mb-4">
              <UnitCounter
                drug={drug}
                packQuantity={quantity.packQuantity}
                unitQuantity={quantity.unitQuantity}
                onQuantityChange={handleQuantityChange}
              />
            </div>
            {pillsInUnit !== 1 && (
              <p className="fs-body-reg ">
                <FormattedMessage {...trns.packageContains} />{' '}
                <span className="fs-body-bold text-tundora">
                  <FormattedMessage
                    {...trns.platesAndPills}
                    values={{
                      plates: <span>{drug.quantityUnitInPack}</span>,
                      pills: <span>{pillsInUnit}</span>
                    }}
                  />
                </span>
              </p>
            )}
            <ButtonSecondary
              disabled={
                quantity.packQuantity === 0 && quantity.unitQuantity === 0
              }
              onClick={() => {
                handleAddItemToCart(drug);
              }}
              className="w-[181px] mb-1 mt-6 s:mt-8"
              size={ButtonSecondarySize.xl}
              label={
                <span className="uppercase">
                  <FormattedMessage {...trns.next} />
                </span>
              }
            />
          </div>
        </Modal>
      </div>
    </>
  );
};

export default SpecifyQuantityModal;
